/* eslint-disable max-len */
import React, { useState, useContext } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  QueryContext,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { Image } from '@thd-olt-component-react/core-ui';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import {
  isAppliance,
  getLoyaltyMembershipInput,
  getRecsBreakPoint,
  getDisplayProducts,
  getPlaceholderTxtLines,
  getRecsTitle,
  hasBadges,
  getRecsDataSource,
  getCarouselSlideProps
} from '../utils/helpers';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { FallbackCard } from './FallbackCard';
import { RecsProductPod } from './RecsProductPod';
import { IRGUtils } from './IRGUtils';
import { VerticalRecsProductPodV7 } from '../vertical-recommendation/VerticalRecsProductPodV7';
import { VerticalRecsPodPlaceholder } from '../vertical-recommendation/VerticalRecsPodPlaceholder';

const { bool: boolType, any, string: stringType, func } = proptypes;

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string()
  })
}, RecsProductPod);
// Wraps a pod in the item ThemeProvider only when the carousel supplies a per-item theme.
// Kept as a module-scope component so the conditional stays out of the render ternary.
// eslint-disable-next-line react/prop-types
const MaybeThemedPod = ({ wrapWithTheme, itemTheme, children }) => {
  if (!wrapWithTheme) {
    return children;
  }
  return (
    <ThemeProvider ssr theme={itemTheme}>
      {children}
    </ThemeProvider>
  );
};

// Renders the fallback card or the loading placeholder while the carousel is loading.
// Module scope keeps the loading branches out of the carousel's own render.
// eslint-disable-next-line react/prop-types
const CarouselLoadingState = ({
  showLoading, loading, showFallback, userId, products, fallbackInfo, renderPlaceholder
}) => {
  if (!showLoading || !loading) {
    return null;
  }
  if (showFallback && !userId && !products) {
    return (
      <FallbackCard
        imageUrl={fallbackInfo.imageUrl}
        header={fallbackInfo.header}
        subheader={fallbackInfo.subheader}
        footerUrl={fallbackInfo.footerUrl}
        footer={fallbackInfo.footer}
      />
    );
  }
  return renderPlaceholder();
};

// Picks the carousel heading. Module scope keeps the title branches out of the render tree.
// eslint-disable-next-line react/prop-types
const RecsCarouselTitle = ({
  hideTitle, isIRG, isTopSellingRanges, isCategoryTab, collectionName, title, subtitle, noPipSpacing
}) => {
  if (hideTitle || isIRG || isTopSellingRanges) {
    return null;
  }
  if (isCategoryTab && !collectionName) {
    return <CarouselHeader title="More From This Brand" noPipSpacing={noPipSpacing} />;
  }
  return <CarouselHeader title={title} subtitle={subtitle} noPipSpacing={noPipSpacing} />;
};

const ProductPodV7Carousel = (
  {
    anchorImage,
    data = null,
    hideTitle = false,
    loading = false,
    scheme = 'drecs',
    showLoading = true,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle = null,

    //  Displaying the category tabs
    isCategoryTab = false,

    hideATC = false,
    hideFavorites = true,
    hideBadge = false,
    membershipInformation,
    zipCode,
    slidesPer,
    slidesToShow,
    hideSwatches = true,
    hideCarouselArrows = false,
    analyticsAnchorProductSku = '',
    parent,
    zoneName,
    zonePosition,
    podResults,
    brandTitleMaxLine,
    hideRating = false,
    isIRG,
    noATCFulfillment = true,
    tntTitle,
    isPipsem,
    hidePrice = false,
    setVisualTabsCategoriesData = null,
    theme = null,
    shouldResetTheme = false,
    enableRoundedProductImages = false,
    enableTopSellingRanges = false,
    nValue,
    telemetryKey,
    noPipSpacing = false
  }
) => {
  const { channel, customer } = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext) || {};

  const isB2BCustomer = customer?.type === 'b2b';
  const isMobile = channel === 'mobile';

  const qtxDefaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: getLoyaltyMembershipInput(membershipInformation),
    skipFavoriteCount: !!isB2BCustomer,
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  const { products, metadata, categories } = data || {};

  const classes = classNames('sui-grid', 'sui-gap-4', 'sui-grid-cols-1', { 'sui-grid-cols-3': anchorImage && !isIRG && !isMobile });

  const colSpanClasses = classNames({ 'sui-col-span-2': anchorImage });

  let title = getRecsTitle(tntTitle, metadata);

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts = getDisplayProducts(isCategorySelected, filtered, products);

  let breakpoints = ({ ...getRecsBreakPoint(slidesToShow, slidesPer, anchorImage) });

  let placeholderTxtLines = getPlaceholderTxtLines(isMobile, isCategoryTab, anchorImage, zoneName, hideATC);

  if (isIRG && (products && products.length < 3)) {
    return null;
  }

  const isTopSellingRanges = enableTopSellingRanges && scheme === 'pip_alternatives';
  const badgeCheck = !isTopSellingRanges && hasBadges(displayProducts, hideBadge);
  const itemTheme = shouldResetTheme ? 'ThdDefaultLightTheme' : theme;
  const processedItemTheme = getThemeByName(itemTheme);
  const shouldWrapItemWithTheme = Boolean(itemTheme);
  // Themed pods have historically not received enableTopSellingRanges; preserved deliberately.
  const topSellingRangesProp = shouldWrapItemWithTheme ? {} : { enableTopSellingRanges };

  const placeholder = () => {
    return isPipsem ? (
      <VerticalRecsPodPlaceholder
        placeholderLines={3}
        placeholderCount={isMobile ? 1 : 3}
      />
    )
      : <LoadingPlaceholder txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />;
  };
  return (
    <div>
      <CarouselLoadingState
        showLoading={showLoading}
        loading={loading}
        showFallback={showFallback}
        userId={userId}
        products={products}
        fallbackInfo={fallbackInfo}
        renderPlaceholder={placeholder}
      />
      {isTopSellingRanges && !hideTitle && (
        <CarouselHeader title={title} subtitle={subtitle} noPipSpacing={noPipSpacing} />
      )}
      {displayProducts?.length > 0 && (
        <>
          <RecsCarouselTitle
            hideTitle={hideTitle}
            isIRG={isIRG}
            isTopSellingRanges={isTopSellingRanges}
            isCategoryTab={isCategoryTab}
            collectionName={metadata?.collectionName}
            title={title}
            subtitle={subtitle}
            noPipSpacing={noPipSpacing}
          />
          {isIRG && (
            <IRGUtils
              products={products}
              scheme={scheme}
              isIRG={isIRG}
              isCategoryTab={isCategoryTab}
              categories={categories}
              pullFilteredData={pullFilteredData}
              tntTitle={tntTitle}
              setVisualTabsCategoriesData={setVisualTabsCategoriesData}
              metadataTitle={metadata?.title}
              noPipSpacing={noPipSpacing}
            />
          )}

          <div className={classNames(classes)}>
            {!isMobile && anchorImage && (
              <div>
                <Image
                  src={anchorImage}
                  alt="main-product-image"
                  height="400"
                  width="400"
                  data-testid="anchor-image"
                  className="sui-w-full"
                />
                <div className="sui-bg-strong sui-text-center sui-p-1 sui-mb-4 sui-text-sm">Current Item</div>
              </div>
            )}
            <div className={colSpanClasses}>
              <QueryProvider
                dataSource={getRecsDataSource(isTopSellingRanges, scheme)}
                cacheKey={`${scheme}-recommendations-carousel`}
                defaultVariables={qtxDefaultVariables}
              >
                <Carousel
                  breakpoints={breakpoints}
                  hideCarouselArrows={hideCarouselArrows}
                  disableMargin
                  layout="full-bleed"
                  containerId={scheme}
                  // Pipsem shows one slide; mobile advances two at a time
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...getCarouselSlideProps(isPipsem, isMobile)}
                >
                  {displayProducts.map((dynamicProduct, idx) => {
                    const { product } = dynamicProduct;
                    const { itemId } = product;
                    const { canonicalUrl } = product?.identifiers || {};
                    const { preferredPriceFlag } = product?.pricing || {};
                    return (
                      <Wrapper key={idx}>
                        {isPipsem ? (
                          <VerticalRecsProductPodV7
                            itemId={itemId}
                            position={idx}
                            storeId={storeId}
                            parent={parent}
                            preferredPriceFlag={preferredPriceFlag}
                            scheme={scheme}
                            anchorProduct={analyticsAnchorProductSku}
                            strategy={dynamicProduct?.strategy}
                            brandTitleMaxLine={brandTitleMaxLine}
                            hideRating={hideRating}
                            hideBadge={hideBadge}
                            hideATC={hideATC}
                            noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                            isMobile={isMobile}
                            url={canonicalUrl}
                            product={product}
                            telemetryKey={telemetryKey}
                          />
                        ) : (
                          <MaybeThemedPod wrapWithTheme={shouldWrapItemWithTheme} itemTheme={processedItemTheme}>
                            <RecsProductPod
                              itemId={itemId}
                              key={itemId}
                              position={idx}
                              storeId={storeId}
                              scheme={scheme}
                              anchorProduct={analyticsAnchorProductSku}
                              parent={parent}
                              zoneName={zoneName}
                              zonePosition={zonePosition}
                              podResults={podResults}
                              hideSwatches={hideSwatches}
                              hideFavorites={hideFavorites}
                              hideBadge={badgeCheck}
                              hideATC={hideATC}
                              hidePrice={hidePrice}
                              hideRating={hideRating}
                              preferredPriceFlag={preferredPriceFlag}
                              brandTitleMaxLine={brandTitleMaxLine}
                              strategy={dynamicProduct?.strategy}
                              noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                              url={canonicalUrl}
                              theme={theme}
                              isMobile={isMobile}
                              enableRoundedProductImages={enableRoundedProductImages}
                              nValue={nValue}
                              product={product}
                              telemetryKey={telemetryKey}
                              {...topSellingRangesProp}
                            />
                          </MaybeThemedPod>
                        )}
                      </Wrapper>
                    );
                  }
                  )}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string()
}, RecsProductPod.dataModel.product);

export const wrappedProduct1 = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  strategy: string(),
  score: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string()
  })
});
ProductPodV7Carousel.dataModel = extend(Wrapper, wrappedProduct1);

ProductPodV7Carousel.displayName = 'ProductPodV7Carousel';

ProductPodV7Carousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  isCategoryTab: boolType,
  hideSwatches: boolType,
  hideCarouselArrows: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType,
  noATCFulfillment: boolType,
  hidePrice: boolType,
  setVisualTabsCategoriesData: func,
  theme: stringType,
  shouldResetTheme: stringType,
  enableRoundedProductImages: boolType,
  noPipSpacing: boolType,
  parent: stringType.isRequired,
  telemetryKey: stringType.isRequired
};

export { ProductPodV7Carousel };
